import React, { SyntheticEvent } from 'react';
import { FaEdit, FaPlusCircle, FaTrash } from 'react-icons/fa';

import Price from 'components/common/Price';
import LaborRate from 'models/LaborRateModel';

import axios from 'helpers/axios';
import config from 'helpers/config';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import NewDropdown from 'components/common/NewDropdown';
import PriceInputCents from 'components/common/PriceInputCents';
import { Edit, Info, Plus, Star, Trash2 } from 'lucide-react';

interface Props {}

interface State {
    laborRates: LaborRate[];
    isCreateNewLaborRateActive: boolean;
    editableRateId: string | null;
    name: string | null;
    rateCents: number | null;
    isDefaultRate: boolean;
}

class LaborRateSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            laborRates: [],
            isCreateNewLaborRateActive: false,
            editableRateId: null,
            rateCents: null,
            name: null,
            isDefaultRate: false,
        };

        this.handleChange = this.handleChange.bind(this);
        this.createNewRate = this.createNewRate.bind(this);
        this.updateRate = this.updateRate.bind(this);
        this.handlePriceChange = this.handlePriceChange.bind(this);
    }

    componentDidMount() {
        this.getLaborRates();
    }

    getLaborRates() {
        axios.get(`${config.apiUrl}/laborRate`).then((res) => this.setState({ laborRates: res.data }));
    }

    createNewRate() {
        const { name, rateCents } = this.state;

        const laborRate = {
            name,
            rateCents: rateCents || 0,
        };

        axios.post(`${config.apiUrl}/laborRate`, laborRate).then((_) => {
            this.getLaborRates();
            this.setState({ name: null, rateCents: null, isCreateNewLaborRateActive: false });
        });
    }

    updateRate() {
        const { name, rateCents, editableRateId } = this.state;
        const laborRate = {
            name,
            rateCents: rateCents || 0,
            id: editableRateId,
        };

        // if (this.state.isDefaultRate && editableRateId) {
        //     this.updateDefaultRate(editableRateId);
        // }

        axios.put(`${config.apiUrl}/laborRate`, laborRate).then((_) => {
            this.setState(
                { name: null, rateCents: null, isCreateNewLaborRateActive: false, editableRateId: null },
                this.getLaborRates,
            );
        });
    }

    handlePriceChange(cents: number) {
        this.setState({ rateCents: cents });
    }

    getEditableRow(isEdit: boolean) {
        return (
            <>
                <td className="px-6 py-4 whitespace-nowrap">
                    <input
                        name="name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        onChange={this.handleChange}
                        value={this.state.name || ''}
                        placeholder="Enter rate name"
                        autoFocus
                    />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <PriceInputCents
                        initialPriceCents={this.state.rateCents || 0}
                        handlePriceChange={this.handlePriceChange}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm text-right"
                    />
                </td>
                {/* Set as default */}
                <td className="px-6 py-4 whitespace-nowrap">
                    {this.state.isDefaultRate ? (
                        <div className="flex items-center space-x-1">
                            <Star className="h-4 w-4 text-amber-500 fill-current" />
                            <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                Default
                            </span>
                        </div>
                    ) : (
                        <span
                            className="text-gray-600 clickable hover:underline"
                            onClick={() => this.updateDefaultRate(this.state.editableRateId || '')}
                        >
                            Set default
                        </span>
                    )}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end space-x-2">
                        <button
                            onClick={() =>
                                this.setState({
                                    editableRateId: null,
                                    isCreateNewLaborRateActive: false,
                                    name: null,
                                    rateCents: null,
                                })
                            }
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-semibold rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={isEdit ? this.updateRate : this.createNewRate}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-semibold rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                        >
                            Save
                        </button>
                    </div>
                </td>
            </>
        );
    }

    handleDelete(laborRate: LaborRate) {
        displayConfirmationModal(`labor rate: "${laborRate.name}"`)
            .then(() => {
                axios.delete(`${config.apiUrl}/laborRate/${laborRate.id}`).then(() => {
                    this.getLaborRates();
                    this.setState({ name: null, rateCents: null, isCreateNewLaborRateActive: false });
                });
            })
            .catch((_) => _);
    }

    editRate(laborRate: LaborRate) {
        this.setState({
            editableRateId: laborRate.id,
            name: laborRate.name,
            rateCents: laborRate.rateCents,
            isCreateNewLaborRateActive: false,
            isDefaultRate: laborRate.isDefault,
        });
    }

    handleChange(e: SyntheticEvent) {
        // @ts-ignore
        const { name, value } = e.target;
        // @ts-ignore
        this.setState({ [name]: value });
    }

    updateDefaultRate(rateId: string) {
        axios.put(`${config.apiUrl}/laborRate/defaultRate/${rateId}`).then((_) => {
            this.setState(
                { name: null, rateCents: null, isCreateNewLaborRateActive: false, editableRateId: null },
                this.getLaborRates,
            );
        });
    }

    render() {
        const { laborRates, isCreateNewLaborRateActive, editableRateId } = this.state;
        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Labor Rates</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Labor Rates</h2>
                            <p className="text-gray-600 mt-1">
                                Configure hourly labor rates for different service tiers
                            </p>
                        </div>
                        <button
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                            onClick={() =>
                                this.setState({
                                    isCreateNewLaborRateActive: !isCreateNewLaborRateActive,
                                    editableRateId: null,
                                    name: '',
                                    rateCents: 0,
                                })
                            }
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add New Rate</span>
                        </button>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-blue-500 text-white">
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Rate
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Default
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {laborRates?.map((laborRate, index) => {
                                        return laborRate.id === editableRateId ? (
                                            <tr key={laborRate.id} className="bg-blue-50">
                                                {this.getEditableRow(true)}
                                            </tr>
                                        ) : (
                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                    {laborRate.name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <Price price={laborRate.rateCents} isInCents={true} />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {laborRate.isDefault && (
                                                        <div className="flex items-center space-x-1">
                                                            <Star className="h-4 w-4 text-amber-500 fill-current" />
                                                            <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                                                Default
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                    <div className="flex justify-end">
                                                        <button
                                                            onClick={() => this.editRate(laborRate)}
                                                            className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                                                            title="Edit"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => this.handleDelete(laborRate)}
                                                            className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {isCreateNewLaborRateActive && (
                                        <tr className="bg-blue-50">{this.getEditableRow(false)}</tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Default Rate Display */}
                    {laborRates.find((rate) => rate.isDefault) && (
                        <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Default Rate</h3>
                                    <p className="text-sm text-gray-600">
                                        This rate will be automatically applied to new service orders
                                    </p>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-green-600">
                                        <Price
                                            price={laborRates.find((rate) => rate.isDefault)?.rateCents || 0}
                                            isInCents={true}
                                        />
                                    </div>
                                    <div className="text-md text-blue-700 font-semibold">
                                        {laborRates.find((rate) => rate.isDefault)?.name}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Info Panel */}
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex gap-3">
                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-2">How labor rates work</h4>
                                <ul className="space-y-1 text-blue-700">
                                    <li>• Labor rates define the hourly cost for different service tiers</li>
                                    <li>• The default rate is automatically applied to new repair orders</li>
                                    <li>• You can override the rate for individual orders or customers</li>
                                    <li>• Different rates allow you to offer premium services or customer discounts</li>
                                    <li>
                                        • Rates are used to calculate labor costs when creating estimates and invoices
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default LaborRateSettings;
